/**
 * Size guide tabs (snippets/hp-pdp-size-guides-modal.liquid).
 *
 * The size charts matched with the product are rendered by Liquid, so the modal opens instantly: the chart of its fit
 * alone, or the charts of its product group when the fit is not matched. Nothing else is loaded for them (no
 * section-url attribute).
 *
 * Only when the product is not matched with any group, every static chart is fetched once, the first time the modal
 * is opened, with the Section Rendering API (sections/hp-size-charts.liquid) and appended as tabs. When Liquid
 * rendered nothing, the fetched section is the whole content of the modal.
 */
(() => {
  if (window.customElements.get('hp-size-guide-tabs')) return;

  // The section does not depend on the product, so a single request is shared by the whole page.
  const requests = new Map();

  const fetchSection = (url) => {
    if (!requests.has(url)) {
      requests.set(
        url,
        fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error(`Size charts request failed: ${response.status}`);
            return response.text();
          })
          .catch((error) => {
            requests.delete(url);
            throw error;
          })
      );
    }

    return requests.get(url);
  };

  class HpSizeGuideTabs extends HTMLElement {
    connectedCallback() {
      if (!this._initialized) {
        this._initialized = true;
        this._tabList = this.querySelector('[data-size-guide-tablist]');
        this._panelList = this.querySelector('[data-size-guide-panels]');
        this._status = this.querySelector('[data-size-guide-status]');
        // The chart of the product, as selected by Liquid: the modal comes back to it every time it is opened
        this._defaultKey = this.selectedTab?.dataset.sizeGuideKey;

        this._tabList.addEventListener('click', (event) => {
          const tab = event.target.closest('[data-size-guide-tab]');
          if (tab) this.activate(tab);
        });
        this._tabList.addEventListener('keydown', this._onKeyDown.bind(this));
        this._tabList.addEventListener('wheel', this._onWheel.bind(this), { passive: false });

        this._setup();
      }

      // xo-modal flags itself with xo-active once it is open, however it was opened (click, keyboard, xoModal.open).
      this._modal = this.closest('xo-modal');

      if (this._modal) {
        this._observer = new MutationObserver(() => {
          if (this._isOpen()) this._onOpen();
          // Once closed, go back to the chart of the product so that the next opening starts from it
          else this._selectDefault();
        });
        this._observer.observe(this._modal, { attributes: true, attributeFilter: ['xo-active'] });
      }
    }

    disconnectedCallback() {
      this._observer?.disconnect();
    }

    get tabs() {
      return Array.from(this._tabList.querySelectorAll('[data-size-guide-tab]'));
    }

    get selectedTab() {
      return this._tabList.querySelector('[data-size-guide-tab][aria-selected="true"]');
    }

    /**
     * Select a tab and show its panel
     */
    activate(tab, { focus = false } = {}) {
      const changed = tab !== this.selectedTab;

      this.tabs.forEach((item) => {
        const isSelected = item === tab;
        const panel = this._panelFor(item);

        item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        item.tabIndex = isSelected ? 0 : -1;
        if (panel) panel.hidden = !isSelected;
      });

      if (focus) tab.focus({ preventScroll: true });

      // Charts have very different heights: start the newly shown one from its top
      if (changed) this._panelList.scrollTop = 0;

      if (this._isOpen()) this.warm();

      this._revealTab(tab);
    }

    /**
     * Load the image of the selected chart right away instead of waiting for the lazy loading. Besides the tab
     * changes, it is called when the customer hovers, focuses or touches the trigger, so that the chart is already
     * there when the modal opens.
     */
    warm() {
      const selectedTab = this.selectedTab;
      const image = selectedTab ? this._panelFor(selectedTab)?.querySelector('img') : null;

      if (image && image.loading === 'lazy') image.loading = 'eager';
    }

    /**
     * Fetch all the static size charts and append the ones that Liquid did not render
     */
    load() {
      const url = this.getAttribute('section-url');

      if (!url || this._loaded) return Promise.resolve();

      if (!this._request) {
        this._setStatus('loading');

        this._request = fetchSection(url)
          .then((html) => {
            this._append(html);
            this._loaded = true;
            this._setStatus(null);
          })
          .catch(() => {
            // The request is retried the next time the modal is opened
            this._request = null;
            this._setStatus('error');
          });
      }

      return this._request;
    }

    _isOpen() {
      const modal = this._modal;

      return !!modal && modal.hasAttribute('xo-active') && modal.getAttribute('xo-active') !== 'false';
    }

    _onOpen() {
      this._selectDefault();
      this.warm();
      this.load();
      this._revealTab(this.selectedTab, 'auto');
    }

    _selectDefault() {
      const tab = this.tabs.find((item) => item.dataset.sizeGuideKey === this._defaultKey);

      if (tab && tab !== this.selectedTab) this.activate(tab);
    }

    _panelFor(tab) {
      return Array.from(this._panelList.children).find(
        (panel) => panel.dataset.sizeGuideKey === tab.dataset.sizeGuideKey
      );
    }

    _append(html) {
      const fragment = new DOMParser().parseFromString(html, 'text/html');
      const knownKeys = new Set(this.tabs.map((tab) => tab.dataset.sizeGuideKey));
      const panels = Array.from(fragment.querySelectorAll('[data-size-guide-panel]'));

      fragment.querySelectorAll('[data-size-guide-tab]').forEach((tab) => {
        const key = tab.dataset.sizeGuideKey;
        const panel = panels.find((item) => item.dataset.sizeGuideKey === key);

        if (!key || !panel || knownKeys.has(key)) return;

        knownKeys.add(key);
        tab.setAttribute('aria-selected', 'false');
        panel.hidden = true;

        this._tabList.append(tab);
        this._panelList.append(panel);
      });

      this._setup();
    }

    /**
     * Link tabs and panels, and make sure that exactly one tab is selected. It is called again once new tabs are
     * added.
     */
    _setup() {
      const tabs = this.tabs;
      const prefix = this.closest('xo-modal')?.getAttribute('xo-name') || 'hp-pdp-size-guide';

      tabs.forEach((tab) => {
        const panel = this._panelFor(tab);
        const key = tab.dataset.sizeGuideKey;

        tab.id = `${prefix}-tab-${key}`;

        if (panel) {
          panel.id = `${prefix}-panel-${key}`;
          panel.setAttribute('aria-labelledby', tab.id);
          tab.setAttribute('aria-controls', panel.id);
        }
      });

      // A single tab is only shown as a tab when more of them are about to be loaded
      this._tabList.hidden = tabs.length === 0 || (tabs.length === 1 && !this.hasAttribute('section-url'));

      if (tabs.length > 0) this.activate(this.selectedTab || tabs[0]);
    }

    /**
     * The loading and error messages are only needed when there is nothing else to show
     */
    _setStatus(state) {
      if (!this._status) return;

      this._status.hidden = state === null || this.tabs.length > 0;
      this._status.querySelector('[data-size-guide-loading]').hidden = state !== 'loading';
      this._status.querySelector('[data-size-guide-error]').hidden = state !== 'error';
    }

    /**
     * Scroll the tab list (and only the tab list) so that the given tab is centered
     */
    _revealTab(tab, behavior = 'smooth') {
      if (!tab || this._tabList.scrollWidth <= this._tabList.clientWidth) return;

      this._tabList.scrollTo({
        left: tab.offsetLeft - (this._tabList.clientWidth - tab.offsetWidth) / 2,
        behavior,
      });
    }

    /**
     * As per https://www.w3.org/WAI/ARIA/apg/patterns/tabs/, arrows move between the tabs
     */
    _onKeyDown(event) {
      const tabs = this.tabs;
      const index = tabs.indexOf(document.activeElement);

      if (index === -1) return;

      let nextIndex;

      switch (event.key) {
        case 'ArrowLeft':
          nextIndex = (index - 1 + tabs.length) % tabs.length;
          break;
        case 'ArrowRight':
          nextIndex = (index + 1) % tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.activate(tabs[nextIndex], { focus: true });
    }

    /**
     * With a mouse, the wheel scrolls the (long) tab list sideways
     */
    _onWheel(event) {
      const list = this._tabList;

      if (list.scrollWidth <= list.clientWidth || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

      event.preventDefault();
      list.scrollLeft += event.deltaY;
    }
  }

  window.customElements.define('hp-size-guide-tabs', HpSizeGuideTabs);

  // The trigger is re-rendered on every variant change, hence the delegation
  const warmSizeGuide = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-size-guide-trigger]') : null;
    const name = trigger?.getAttribute('xo-name');

    if (!name) return;

    document.querySelector(`xo-modal[xo-name="${CSS.escape(name)}"] hp-size-guide-tabs`)?.warm();
  };

  ['pointerover', 'focusin', 'touchstart'].forEach((type) => {
    document.addEventListener(type, warmSizeGuide, { passive: true });
  });
})();
